import {
  GetPhoneOptionsResponse,
  ValidationErrors,
} from "../../../lib/api/models";
import styles from "./ModifyPhonesRestriction.module.css";
import Toggle from "../../common/Toggle";
import Tooltip from "../../common/Tooltip";
import classNames from "classnames";
import StyledCheckbox from "../../common/inputs/StyledCheckbox";
import Select from "../../common/inputs/Select";
import StyledRadioButton from "../../common/inputs/StyledRadioButton";
import DateInput from "../../common/inputs/DateInput";
import { PhoneNumberFormInput } from "../PhoneNumber.hooks";
import ValidationMessage from "../../common/ValidationMessage/ValidationMessage";

const strings = {
  restrictedUseYes: "Restricted Use: Yes",
  restrictedUseNo: "Restricted Use: No",
  tooltipText:
    "A number is set to restricted use when requested that we only call specific numbers on specific days or with specific instructions. Restricted Use only impacts active numbers, as inactive numbers are not loaded in the dialer file.",
  restrictedDays: "Restricted Days",
  none: "None",
  callFrequency: "Call Frequency",
  restrictionLiftDate: "Restriction Lift Date",
  restrictionLiftDateAriaLabel: "Date input for restriction lift date",
  selectAll: "Select All",
  select: "Select",
  never: "Never",
};

const MAX_DATE_RANGE = "9999-12-31";

type Props = {
  options: GetPhoneOptionsResponse;
  formState: PhoneNumberFormInput;
  generalDispatch: (payload: Partial<PhoneNumberFormInput>) => void;
  validationMessages?: ValidationErrors;
  disabled?: boolean;
};

export default function ModifyPhonesRestriction({
  options,
  formState: {
    restricted,
    restrictionDays,
    restrictionFrequency,
    restrictionLiftDate,
  },
  generalDispatch,
  validationMessages,
  disabled,
}: Props) {
  const selectableDays = options.restrictionDays.filter((o) => o.selectable);

  return (
    <div className={classNames(styles.container, styles.fontSize)}>
      <div className={styles.statusLine}>
        <Toggle
          checked={restricted}
          onChange={(e) => {
            generalDispatch({ restricted: e.target.checked });
          }}
          label={
            restricted ? strings.restrictedUseYes : strings.restrictedUseNo
          }
          disabled={disabled}
        />

        <Tooltip>
          <p>{strings.tooltipText}</p>
        </Tooltip>
      </div>

      {restricted && (
        <div className={styles.contentAndValidation}>
          <div className={styles.content}>
            <div>
              <StyledCheckbox
                label={strings.selectAll}
                className={styles.bold}
                checked={restrictionDays.length === selectableDays.length}
                onChange={() =>
                  generalDispatch({
                    restrictionDays:
                      restrictionDays.length === selectableDays.length
                        ? []
                        : options.restrictionDays.map((o) => o.id),
                    restricted: true,
                  })
                }
                disabled={disabled}
              />
              {selectableDays.map((day) => (
                <StyledCheckbox
                  key={day.id}
                  className={styles.bold}
                  label={day.value}
                  checked={restrictionDays.includes(day.id)}
                  onChange={() =>
                    generalDispatch({
                      restrictionDays: restrictionDays.includes(day.id)
                        ? restrictionDays.filter((d) => d !== day.id)
                        : [...restrictionDays, day.id],
                      restricted: true,
                    })
                  }
                  disabled={disabled}
                />
              ))}
              {validationMessages?.restrictionDays && (
                <ValidationMessage
                  validationMessages={validationMessages.restrictionDays}
                />
              )}
            </div>
            <div>
              <Select
                labelClassName={classNames(
                  styles.selectLabel,
                  styles.bold,
                  styles.fontSize,
                )}
                label={strings.callFrequency}
                placeholder={strings.select}
                selectedValue={restrictionFrequency}
                onChange={(v) =>
                  generalDispatch({ restrictionFrequency: v, restricted: true })
                }
                options={options.restrictionFrequencies
                  .filter((o) => o.selectable)
                  .map((o) => ({
                    key: o.id,
                    value: o.id,
                    label: o.value,
                  }))}
                validationMessages={validationMessages?.restrictionFrequency}
                disabled={disabled}
              />
              <span className={styles.bold}>{strings.restrictionLiftDate}</span>
              <StyledRadioButton
                className={styles.dateRadioButton}
                label={
                  <DateInput
                    inputClassName={styles.noMargin}
                    aria-label={strings.restrictionLiftDate}
                    value={restrictionLiftDate ?? ""}
                    onChangeDate={(v) =>
                      generalDispatch({
                        restrictionLiftDate: v,
                      })
                    }
                    max={MAX_DATE_RANGE}
                  />
                }
                aria-label={strings.restrictionLiftDateAriaLabel}
                checked={restrictionLiftDate !== null}
                onChange={() =>
                  generalDispatch({
                    restrictionLiftDate: "",
                  })
                }
                disabled={disabled}
              />
              <StyledRadioButton
                label={strings.never}
                checked={restrictionLiftDate === null}
                onChange={() =>
                  generalDispatch({
                    restrictionLiftDate: null,
                  })
                }
                disabled={disabled}
              />
              {validationMessages?.restrictionLiftDate && (
                <ValidationMessage
                  validationMessages={validationMessages.restrictionLiftDate}
                />
              )}
            </div>
          </div>
          {validationMessages?.restricted && (
            <ValidationMessage
              validationMessages={validationMessages.restricted}
            />
          )}
        </div>
      )}
    </div>
  );
}
